import React, {useEffect,  useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi, usePlayMakersConfig} from "../PlayMakersProvider";
import {SchemaExtendedType, SchemaPayloadType, SchemaType} from "../types";
import {usePagination} from "./usePagination";

type SchemaPushMethod = (updatedSchema?: Partial<SchemaPayloadType & {newThumbnailFile: File}>) => Promise<SchemaType>;
type SchemaFetchMethod = (quiet?: boolean) => Promise<SchemaType>;
type SchemaPushThumbnailMethod = (file: File | null) => Promise<void>;

interface UseSchemaReturn extends Partial<SchemaType> {
  schema: SchemaType | null;
  displayInteractive: SchemaPayloadType["displayInteractive"];
  displayThumbnail: SchemaPayloadType["displayThumbnail"];
  fetched: boolean;
  setSchema: React.Dispatch<React.SetStateAction<SchemaType | null>>;
  setThumbnail: React.Dispatch<React.SetStateAction<File | null>>;
  push: SchemaPushMethod;
  pushThumbnail: SchemaPushThumbnailMethod;
  refresh: SchemaFetchMethod;
}

export const useSchema = (id?: string): UseSchemaReturn => {
  const {createSchema, getSchema, updateSchema, updateSchemaThumbnail} = useApi();

  let schemaId = id;

  const [schema, setSchema] = useState<SchemaType | null>(null);
  const [fetched, setFetched] = useState<boolean>(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);

  const fetchSchema: SchemaFetchMethod = async(quiet = false) => {
    try {
      if(!id) {
        setSchema(null);
        setFetched(false);
        throw new Error("No schema to fetch");
      }

      if(!quiet) setFetched(false);
      const newSchema = await withCache(getSchema, [id], 120_000);
      setSchema(newSchema);
      setFetched(true);
      return newSchema;
    } catch (error) {
      console.error(`Error fetching schema ${id}`, error);
      throw new Error(`Error fetching schema ${id}`, {cause: error});
    }
  };

  const pushThumbnail: SchemaPushThumbnailMethod = async(file = thumbnail) => {
    if(!file || typeof file === "string") return;
    try {
      if(!schemaId) throw new Error("No schema to push thumbnail to");
      await updateSchemaThumbnail(schemaId, file);
      clearCache("getSchema", [schemaId]);
    } catch (error) {
      console.error(`Error updating thumbnail for schema '${schemaId}'`, error);
      throw new Error(`Error updating thumbnail for schema '${schemaId}'`, {cause: error});
    }
  };

  const ignoredOpenStateKeys: Array<keyof SchemaPayloadType> = ["data"];

  const pushSchema: SchemaPushMethod = async updatedSchema => {
    const newSchema = {...schema, ...updatedSchema};

    if(!newSchema) throw new Error("No schema to push");

    const {newThumbnailFile} = newSchema;
    if(newThumbnailFile) delete newSchema.newThumbnailFile;

    const currentState = schema?.state;
    const payload: Partial<SchemaPayloadType> = {
      ...newSchema,
      description: newSchema.description || "",
      displayInteractive: newSchema.displayInteractive || "{}",
      displayThumbnail: newSchema.displayThumbnail || "{}",
      state: newSchema.state || "draft"
    };

    if(currentState === "open" || currentState === "closed")
      ignoredOpenStateKeys.forEach(key => delete payload[key]);

    try {
      const schema = id
        ? await updateSchema(id, payload)
        : await createSchema(payload as SchemaPayloadType);
      if(!schema) throw new Error("Schema not uploaded");
      schemaId = schema?.id;
      if(newThumbnailFile || thumbnail)
        await pushThumbnail(newThumbnailFile || thumbnail);
      clearCache("getSchema", [schemaId]);
      clearCache("getSchemas");
      setSchema(schema);
      return schema;
    } catch (error) {
      console.error(`Error pushing schema '${id}'`, error);
      throw new Error(`Error pushing schema '${id}'`, {cause: error});
    }
  };

  useEffect(() => {
    fetchSchema();
  }, [id]);

  return {
    ...schema,
    displayInteractive: schema?.displayInteractive || "",
    displayThumbnail: schema?.displayThumbnail || "",
    fetched,
    push: pushSchema,
    pushThumbnail,
    refresh: fetchSchema,
    schema,
    setSchema,
    setThumbnail
  };
};

type SchemaFetchMoreMethod = (refresh?: boolean) => Promise<void>;
type SchemasFetchMethod = (quiet?: boolean) => Promise<SchemaExtendedType[]>;

interface UseSchemasReturn {
  schemas: SchemaExtendedType[];
  fetched: boolean;
  hasMore: boolean;
  currentPage: number;
  fetchMore: SchemaFetchMoreMethod;
  refresh: SchemasFetchMethod;
}

export const useSchemas = (
  params: {[key: string]: any} = {}
): UseSchemasReturn => {
  const {PROJECT_ID} = usePlayMakersConfig();
  const {getSchemas, updateSchema, deleteSchema} = useApi();

  const [schemas, setSchemas] = useState<SchemaExtendedType[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const {appendNextPage, refreshLastPage, checkHasMore, currentPage} =
    usePagination<SchemaExtendedType>(setSchemas, setHasMore, hasMore, params?.page, params?.limit);

  const extendSchemas = (schemas: SchemaType[]): SchemaExtendedType[] =>
    schemas.map(schema => ({
      ...schema,
      delete: () => onDeleteSchema(schema.id),
      pushState: (state: SchemaType["state"]) =>
        pushSchemaState(schema.id, state)
    }));


  const fetchSchemas: SchemasFetchMethod = async(quiet = false) => {
    try {
      if(!PROJECT_ID) throw new Error("No project ID to fetch schemas");

      if(!quiet) setFetched(false);
      const newSchemas = extendSchemas(checkHasMore(await withCache(
        getSchemas,
        [PROJECT_ID, params],
        120_000
      )));
      setSchemas(newSchemas);
      return newSchemas;
    } catch (error) {
      console.error("Error fetching schemas", error);
      throw new Error("Error fetching schemas", {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const fetchMore:SchemaFetchMoreMethod = async refresh => {
    try {
      setFetched(false);
      if(refresh) {
        await refreshLastPage(async appendParams =>
          extendSchemas(await getSchemas(PROJECT_ID, {...params, ...appendParams}))
        );
      } else {
        await appendNextPage(async appendParams =>
          extendSchemas(await withCache(getSchemas, [PROJECT_ID, {...params, ...appendParams}], 120_000))
        );
      }
    } catch (error) {
      console.error("Error fetching more schemas", error);
      throw new Error("Error fetching more schemas", {cause: error});
    } finally {
      setFetched(true);
    }
  };


  useEffect(() => {
    fetchSchemas();
  }, [JSON.stringify(params), PROJECT_ID]);

  const updateSchemaState = (
    schemaId: string,
    updates: Partial<SchemaType>
  ) => {
    setSchemas(prevSchemas =>
      prevSchemas.map(schema =>
        schema.id === schemaId ? {...schema, ...updates} : schema
      )
    );
  };

  const pushSchemaState = async(
    id: string,
    state: SchemaType["state"]
  ): Promise<SchemaExtendedType> => {
    try {
      const newSchema = extendSchemas([await updateSchema(id, {state})])[0];
      if(!newSchema) throw new Error("Schema not uploaded");
      clearCache("getSchema", [id]);
      clearCache("getSchemas");
      updateSchemaState(id, newSchema);
      return newSchema;
    } catch (error) {
      console.error(`Error pushing schema '${id}'`, error);
      throw new Error(`Error pushing schema '${id}'`, {cause: error});
    }
  };

  const onDeleteSchema = async(id: string) => {
    try {
      const resp = await deleteSchema(id);
      clearCache("getSchema", [id]);
      clearCache("getSchemas");
      setSchemas(prevSchemas =>
        prevSchemas.filter(schema => schema.id !== id)
      );
      return resp;
    } catch (error) {
      console.error(`Error deleting schema '${id}'`, error);
      throw new Error(`Error deleting schema '${id}'`, {cause: error});
    }
  };

  return {
    currentPage,
    fetchMore,
    fetched,
    hasMore,
    refresh: fetchSchemas,
    schemas
  };
};

export const useSchemasByState = (state: string, params?: { [key: string]: any }) => {
  const {schemas, ...useSchemasData} = useSchemas({state, ...params});
  return {
    schemas: schemas.filter(schema => schema.state === state),
    ...useSchemasData
  };
};
